import { notification } from 'antd';
import logoutUser from '../utils/logout-user';

/**
 * Centralized error handling service
 * Provides consistent error handling, logging, and user notifications
 */
class ErrorService {
  constructor() {
    this.errorQueue = [];
    this.maxQueueSize = 100;
    this.isProduction = process.env.NODE_ENV === 'production';
  }

  /**
   * Log error to external service (in production)
   * @param {Error} error - The error object
   * @param {Object} context - Additional context information
   */
  logError(errorOrMessage, contextOrError = {}, maybeContext = {}) {
    // Support both signatures:
    // 1) logError(error: Error, context?: Object)
    // 2) logError(message: string, error?: Error|any, context?: Object)
    let error = null;
    let context = {};
    let messageOverride = null;

    if (typeof errorOrMessage === 'string') {
      messageOverride = errorOrMessage;
      // If second arg looks like an Error, treat as error
      if (contextOrError instanceof Error) {
        error = contextOrError;
        context = maybeContext || {};
      } else {
        // No Error provided, treat second arg as context
        context = (contextOrError && typeof contextOrError === 'object') ? contextOrError : {};
      }
    } else {
      error = errorOrMessage instanceof Error
        ? errorOrMessage
        : new Error(errorOrMessage?.message || 'Unknown error');
      context = (contextOrError && typeof contextOrError === 'object') ? contextOrError : {};
    }

    const errorData = {
      message: messageOverride || error?.message || 'Unknown error',
      stack: error?.stack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      userId: context.userId || null,
      component: context.component || null,
      action: context.action || null,
      ...context
    };

    // Add to error queue
    this.errorQueue.push(errorData);
    if (this.errorQueue.length > this.maxQueueSize) {
      this.errorQueue.shift();
    }

    if (this.isProduction) {
      this.sendToErrorTrackingService(errorData);
    } else {
      console.error('Error logged:', errorData);
    }
  }

  /**
   * Send error to external tracking service (Sentry, LogRocket, etc.)
   * @param {Object} errorData - Error data to send
   */
  async sendToErrorTrackingService(errorData) {
    try {
      // Replace with our actual error tracking service
      // Example: Sentry.captureException(error, { extra: errorData });
      
      // For now, we'll use a mock endpoint
      await fetch('/api/errors', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(errorData),
      });
    } catch (sendError) {
      console.error('Failed to send error to tracking service:', sendError);
    }
  }

  /**
   * Handle API errors with appropriate user notifications
   * @param {Error} error - The error object
   * @param {Object} options - Configuration options
   */
  handleApiError(error, options = {}) {
    const {
      showNotification = true,
      component = null,
      action = null,
      fallbackMessage = 'An unexpected error occurred. Please try again.'
    } = options;

    let title = 'Error';
    let message = fallbackMessage;
    let type = 'error';

    if (error.response) {
      const { status, data } = error.response;
      
      switch (status) {
        case 400:
          title = 'Invalid Request';
          message = data?.message || 'The request contains invalid data.';
          break;
        case 401:
          title = 'Authentication Required';
          message = 'Please log in to continue.';
          type = 'warning';
          // Trigger logout if needed
          this.handleAuthenticationError();
          break;
        case 403:
          title = 'Access Denied';
          message = 'You do not have permission to perform this action.';
          type = 'warning';
          break;
        case 404:
          title = 'Not Found';
          message = 'The requested resource was not found.';
          break;
        case 422:
          title = 'Validation Error';
          message = this.formatValidationErrors(data?.errors) || 'Please check your input and try again.';
          break;
        case 429:
          title = 'Too Many Requests';
          message = 'Please wait a moment before trying again.';
          type = 'warning';
          break;
        case 500:
          title = 'Server Error';
          message = 'A server error occurred. Our team has been notified.';
          break;
        case 503:
          title = 'Service Unavailable';
          message = 'The service is temporarily unavailable. Please try again later.';
          type = 'warning';
          break;
        default:
          title = 'Request Failed';
          message = data?.message || fallbackMessage;
      }
    } else if (error.request) {
      title = 'Network Error';
      message = 'Unable to connect to the server. Please check your internet connection.';
      type = 'warning';
    }

    // Log the error
    this.logError(error, { component, action, apiError: true });

    // Show notification to user
    if (showNotification) {
      notification[type]({
        message: title,
        description: message,
        placement: 'topRight',
        duration: type === 'error' ? 6 : 4,
      });
    }

    return { title, message, type };
  }

  
  /**
   * Handle authentication errors
   */
  handleAuthenticationError = (error, store) => {
    try {
      // Notify user
      notification.warning({
        message: 'Session expired',
        description: 'Please log in again to continue.',
        placement: 'topRight',
      });

      // Force a full refresh and redirect to login using centralized logout flow
      logoutUser();
    } catch (e) {
      console.error('Error handling authentication error:', e);
    }
  };

  /**
   * Format validation errors for display
   * @param {Object|Array} errors - Validation errors
   * @returns {string} Formatted error message
   */
  formatValidationErrors(errors) {
    if (!errors) return null;

    if (Array.isArray(errors)) {
      return errors.join(', ');
    }

    if (typeof errors === 'object') {
      const errorMessages = Object.values(errors).flat();
      return errorMessages.join(', ');
    }

    return errors.toString();
  }

  /**
   * Handle form validation errors
   * @param {Object} errors - Form validation errors
   * @param {Object} options - Configuration options
   */
  handleFormErrors(errors, options = {}) {
    const { showNotification = true, component = null } = options;

    if (!errors || typeof errors !== 'object') {
      // Gracefully handle missing or invalid errors object
      this.logError(new Error('Form validation failed'), {
        component,
        formErrors: errors,
        validationError: true
      });

      if (showNotification) {
        notification.error({
          message: 'Validation Error',
          description: 'Please check your input and try again.',
          placement: 'topRight',
          duration: 5,
        });
      }
      return 'Validation error';
    }

    const errorMessages = Object.entries(errors)
      .map(([field, error]) => `${field}: ${error?.message || error}`)
      .join(', ');

    this.logError(new Error('Form validation failed'), {
      component,
      formErrors: errors,
      validationError: true
    });

    if (showNotification) {
      notification.error({
        message: 'Validation Error',
        description: errorMessages,
        placement: 'topRight',
        duration: 5,
      });
    }

    return errorMessages;
  }

  /**
   * Handle component errors (for use in Error Boundaries)
   * @param {Error} error - The error object
   * @param {Object} errorInfo - React error info
   * @param {Object} context - Additional context
   */
  handleComponentError(error, errorInfo, context = {}) {
    const errorData = {
      ...context,
      componentStack: errorInfo?.componentStack || '',
      reactError: true
    };

    this.logError(error, errorData);

    // Don't show notification for component errors as they're handled by Error Boundary UI
    return {
      errorId: Date.now().toString(36) + Math.random().toString(36).substr(2),
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Generic error handler to unify error handling across the app
   * @param {any} error - The error or value thrown
   * @param {Object} context - Additional context and options
   */
  handleError(error, context = {}) {
    const options = {
      showNotification: context.showNotification !== undefined ? context.showNotification : true,
      component: context.component || context.context || null,
      action: context.action || context.userAction || null,
      fallbackMessage: context.fallbackMessage || 'An unexpected error occurred. Please try again.'
    };

    // API/Axios style errors
    if (error && (error.response || error.request || error.isAxiosError)) {
      return this.handleApiError(error, options);
    }

    // Form validation errors provided via context
    if (context.formErrors) {
      return this.handleFormErrors(context.formErrors, { showNotification: options.showNotification, component: options.component });
    }

    // Normalize to an Error instance
    const normalizedError = error instanceof Error
      ? error
      : new Error(typeof error === 'string' ? error : (error?.message || options.fallbackMessage));

    // Log the error
    this.logError(normalizedError, { ...context, genericError: true });

    // Notify user if enabled
    if (options.showNotification) {
      notification.error({
        message: 'Error',
        description: normalizedError.message || options.fallbackMessage,
        placement: 'topRight',
        duration: 5,
      });
    }

    return { title: 'Error', message: normalizedError.message || options.fallbackMessage, type: 'error' };
  }

  /**
   * Get recent errors for debugging
   * @param {number} count - Number of recent errors to return
   * @returns {Array} Recent errors
   */
  getRecentErrors(count = 10) {
    return this.errorQueue.slice(-count);
  }

  /**
   * Clear error queue
   */
  clearErrors() {
    this.errorQueue = [];
  }

  /**
   * Log informational messages
   * @param {string} message - The info message
   * @param {Object} context - Additional context information
   */
  logInfo(message, context = {}) {
    const infoData = {
      message,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      level: 'info',
      ...context
    };

    if (this.isProduction) {
      // In production, you might want to send to analytics service
      console.info('Info logged:', infoData);
    } else {
      // In development, log to console
      console.info('Info logged:', infoData);
    }
  }

  /**
   * Create a wrapper for async functions with error handling
   * @param {Function} fn - The async function to wrap
   * @param {Object} options - Error handling options
   * @returns {Function} Wrapped function
   */
  withErrorHandling(fn, options = {}) {
    return async (...args) => {
      try {
        return await fn(...args);
      } catch (error) {
        this.handleApiError(error, options);
        throw error;
      }
    };
  }
}

// Create singleton instance
const errorService = new ErrorService();

// Make it available globally for debugging
if (typeof window !== 'undefined') {
  window.errorService = errorService;
}

export default errorService;

// Export specific methods for convenience
export const {
  logError,
  logInfo,
  handleApiError,
  handleFormErrors,
  handleComponentError,
  withErrorHandling,
  handleError,
} = errorService;